import { formatDatasetName, datasetSubtypeLabel } from '../../../helpers';
import { datasetTypeIconUrl } from './datasetTypeIcon';

/**
 * Turns fetched lineage data into the element array (nodes + edges + compound group/datasource/
 * system nodes) consumed by the Cytoscape graph.
 * @param {{ nodes: Array<Object>, edges: Array<Object> }} data
 * @param {boolean} [rollupToParent=false] - Reserved for symmetry with `formatRelationships`. The
 *   lineage graph is only rendered on the Dataset view (never a Datasource/DatasetGroup overview),
 *   so no caller passes this today; a lineage-adapted parent rollup (dataset-level edges, no field
 *   pairs to merge) would be implemented here if an overview lineage graph is added.
 * @param {boolean} [nestUnderParentDataset=false] - When true, a node that has an immediate parent
 *   dataset (`parent_dataset_id` + `parent_dataset_name`) is wrapped in a compound node for that
 *   parent, so the dataset hierarchy is visible (used by the Dataset view).
 * @returns {Array<Object>|undefined} Cytoscape elements, or undefined when no data.
 */
export const formatLineage = (data, rollupToParent = false, nestUnderParentDataset = false) => {
  if (data) {
    const nodes = data.nodes;
    const edges = data.edges;

    let compoundNodesDataset = [];
    let compoundNodesLvl1 = [];
    let compoundNodesLvl2 = [];
    let compoundNodesLvl3 = [];

    let fieldCountMax = Number.NEGATIVE_INFINITY;
    let rowCountMax = Number.NEGATIVE_INFINITY;
    let diskSizeMax = Number.NEGATIVE_INFINITY;

    let formattedNodes = nodes.map((node, index) => {
      const upstreamCount = edges.filter(x => x.to_dataset_id === node.dataset_id).length;
      const downstreamCount = edges.filter(x => x.from_dataset_id === node.dataset_id).length;

      let level = edges.find(x => {
        return x.to_dataset_id === node.dataset_id || x.from_dataset_id === node.dataset_id;
      });
      level =
        level &&
        (level.to_dataset_id === node.dataset_id
          ? level.dataset_lineage_level
          : level.dataset_lineage_level + -1);

      const fieldCount = node.field_count;
      const rowCount = parseInt(node.row_count) || Number.NEGATIVE_INFINITY;
      const diskSize = parseInt(node.disk_size) || Number.NEGATIVE_INFINITY;

      if (fieldCountMax < fieldCount) {
        fieldCountMax = fieldCount;
      }
      if (rowCountMax < rowCount) {
        rowCountMax = rowCount;
      }
      if (diskSizeMax < diskSize) {
        diskSizeMax = diskSize;
      }

      compoundNodesLvl1.push({
        data: {
          id: 'dataset_group-' + node.dataset_group_id,
          parent: 'datasource-' + node.datasource_id,
          label: node.dataset_group_name,
          type: 'compound',
        },
      });

      compoundNodesLvl2.push({
        data: {
          id: 'datasource-' + node.datasource_id,
          parent: 'system-' + node.system_id,
          label: node.datasource_name,
          type: 'compound',
        },
      });

      compoundNodesLvl3.push({
        data: {
          id: 'system-' + node.system_id,
          label: node.system_name,
          type: 'compound',
        },
      });

      // In the Dataset view, wrap a node that has an immediate parent dataset in a compound for
      // that parent (nested under the group box), so the dataset hierarchy is shown. Otherwise the
      // node sits directly in its dataset group as before.
      const hasParentDataset =
        nestUnderParentDataset && node.parent_dataset_id != null && node.parent_dataset_name;
      const nodeParentId = hasParentDataset
        ? 'dataset-' + node.parent_dataset_id
        : 'dataset_group-' + node.dataset_group_id;

      if (hasParentDataset) {
        compoundNodesDataset.push({
          data: {
            id: 'dataset-' + node.parent_dataset_id,
            parent: 'dataset_group-' + node.dataset_group_id,
            label: formatDatasetName(
              node.parent_dataset_name,
              datasetSubtypeLabel(
                node.parent_dataset_subtype_code,
                node.parent_dataset_subtype_name,
                node.parent_dataset_type_code
              )
            ),
            type: 'compound',
          },
        });
      }

      return {
        data: {
          id: node.dataset_id + '',
          parent: nodeParentId,
          layer: level,
          order: index,
          dataset_group_id: node.dataset_group_id,
          datasource_id: node.datasource_id,
          system_id: node.system_id,
          label: formatDatasetName(
            node.dataset_name,
            datasetSubtypeLabel(
              node.dataset_subtype_code,
              node.dataset_subtype_name,
              node.dataset_type_code
            )
          ),
          // Name and subtype kept alongside `label` so the popup title and the graph's filter can
          // render the subtype as a pill (the cytoscape `label` itself must stay a plain string).
          // Resolved through datasetSubtypeLabel like `label` is, so both show a pill only where the
          // subtype says something the dataset type does not -- an API endpoint's method. A Power BI
          // item's subtype mirrors its type, so it resolves to nothing and the type icon shows
          // instead.
          dataset_name: node.dataset_name,
          dataset_subtype_name: datasetSubtypeLabel(
            node.dataset_subtype_code,
            node.dataset_subtype_name,
            node.dataset_type_code
          ),
          dataset_subtype_operation: node.dataset_subtype_operation,
          datasource_type_code: node.datasource_type_code,
          dataset_description: node.dataset_description,
          dataset_type_name: node.dataset_type_name,
          // Drawn inside the node by the graph stylesheet; undefined when the type has no icon.
          typeIcon: datasetTypeIconUrl(node.dataset_type_name),
          field_count: node.field_count,
          row_count: node.row_count,
          disk_size: node.disk_size,
          attribute_ratio: node.attribute_ratio,
          upstream_count: upstreamCount,
          downstream_count: downstreamCount,
          type: 'node',
          object_type: node.dataset_type_name,
          object_name: formatDatasetName(
            node.dataset_name,
            datasetSubtypeLabel(
              node.dataset_subtype_code,
              node.dataset_subtype_name,
              node.dataset_type_code
            )
          ),
          object_icon: node.dataset_type_name,
          object_description: node.dataset_description,
        },
        classes: 'node',
      };
    });

    compoundNodesDataset = [
      ...new Map(compoundNodesDataset.map(item => [item.data.id, item])).values(),
    ];
    compoundNodesLvl1 = [...new Map(compoundNodesLvl1.map(item => [item.data.id, item])).values()];
    compoundNodesLvl2 = [...new Map(compoundNodesLvl2.map(item => [item.data.id, item])).values()];
    compoundNodesLvl3 = [...new Map(compoundNodesLvl3.map(item => [item.data.id, item])).values()];

    // The group/datasource/system boxes are only drawn when more than one dataset group is present;
    // with a single group the graph stays flat. Parent-dataset boxes are always kept, but when the
    // group box is hidden they are promoted to the top level (their group parent is removed).
    const showGroups = compoundNodesLvl1.length > 1;

    if (!showGroups) {
      compoundNodesDataset = compoundNodesDataset.map(({ data: { parent, ...rest } }) => ({
        data: rest,
      }));
    }

    formattedNodes = formattedNodes.map(node => {
      // Collapse the group box for single-group graphs, but never drop a parent-dataset box.
      const nestedUnderDataset =
        typeof node.data.parent === 'string' && node.data.parent.startsWith('dataset-');
      if (!showGroups && !nestedUnderDataset) {
        delete node.data.parent;
      }
      return {
        ...node,
        data: {
          ...node.data,
          field_count_max: fieldCountMax,
          row_count_max: rowCountMax,
          disk_size_max: diskSizeMax,
        },
      };
    });

    formattedNodes = [
      ...formattedNodes,
      ...compoundNodesDataset,
      ...(showGroups ? [...compoundNodesLvl1, ...compoundNodesLvl2, ...compoundNodesLvl3] : []),
    ];

    let formattedEdges = edges.map(edge => {
      return {
        data: {
          id: edge.from_dataset_id + '-' + edge.to_dataset_id,
          source: edge.from_dataset_id + '',
          target: edge.to_dataset_id + '',
          type: 'edge',
          source_dataset_name: edge.from_dataset_name,
          source_dataset_type_name: edge.from_dataset_type_name,
          source_dataset_description: edge.from_dataset_description,
          source_dataset_group_id: edge.from_dataset_group_id,
          source_dataset_group_name: edge.from_dataset_group_name,
          source_datasource_id: edge.from_datasource_id,
          source_system_id: edge.from_system_id,
          target_dataset_name: edge.to_dataset_name,
          target_dataset_type_name: edge.to_dataset_type_name,
          target_dataset_description: edge.to_dataset_description,
          target_dataset_group_id: edge.to_dataset_group_id,
          target_dataset_group_name: edge.to_dataset_group_name,
          target_datasource_id: edge.to_datasource_id,
          target_system_id: edge.to_system_id,
          object_type: edge.dataset_lineage_type,
          object_name: edge.dataset_lineage_name,
          level: edge.dataset_lineage_level,
          direction: edge.dataset_lineage_direction,
        },
      };
    });

    return [...formattedNodes, ...formattedEdges];
  }
};
